"use client";

import { useState, useRef, useCallback } from "react";
import Fuse from "fuse.js";
import { createFuseInstance } from "./fuse-search";
import type { SearchIndexProduct } from "./types";

interface UseSearchIndex {
	/** Fuse instance, null until index is loaded */
	fuse: Fuse<SearchIndexProduct> | null;
	/** Raw products array (needed for "Did you mean?" loose search) */
	products: SearchIndexProduct[];
	/** Whether the index is currently being fetched */
	isLoading: boolean;
	/** Load the index (call on search input focus) */
	ensureLoaded: () => void;
}

interface CachedSearchIndex {
	fuse: Fuse<SearchIndexProduct>;
	products: SearchIndexProduct[];
	loadedAtMs: number;
}

export const SEARCH_INDEX_TAB_TTL_MS = 60_000;

const resolvedIndexCache = new Map<string, CachedSearchIndex>();
const pendingIndexCache = new Map<string, Promise<CachedSearchIndex>>();

function isFresh(entry: CachedSearchIndex | undefined, now = Date.now()): entry is CachedSearchIndex {
	return Boolean(entry && now - entry.loadedAtMs < SEARCH_INDEX_TAB_TTL_MS);
}

export function __resetSearchIndexCacheForTests(): void {
	resolvedIndexCache.clear();
	pendingIndexCache.clear();
}

export function useSearchIndex(channel: string): UseSearchIndex {
	const initialEntry = resolvedIndexCache.get(channel);
	const initialCached = isFresh(initialEntry) ? initialEntry : undefined;
	const [isLoading, setIsLoading] = useState(false);
	const [loadedState, setLoadedState] = useState<{
		channel: string;
		fuse: Fuse<SearchIndexProduct> | null;
		products: SearchIndexProduct[];
		loadedAtMs: number | null;
	}>(() => ({
		channel,
		fuse: initialCached?.fuse ?? null,
		products: initialCached?.products ?? [],
		loadedAtMs: initialCached?.loadedAtMs ?? null,
	}));
	const requestVersionRef = useRef(0);

	const stateForChannel = loadedState.channel === channel ? loadedState : null;
	const fuse = stateForChannel?.fuse ?? null;
	const products = stateForChannel?.products ?? [];

	const ensureLoaded = useCallback(() => {
		const requestVersion = ++requestVersionRef.current;
		const cached = resolvedIndexCache.get(channel);
		if (isFresh(cached)) {
			setIsLoading(false);
			setLoadedState((current) =>
				current.channel === channel &&
				current.fuse === cached.fuse &&
				current.products === cached.products &&
				current.loadedAtMs === cached.loadedAtMs
					? current
					: {
							channel,
							fuse: cached.fuse,
							products: cached.products,
							loadedAtMs: cached.loadedAtMs,
						},
			);
			return;
		}

		if (cached) resolvedIndexCache.delete(channel);
		setLoadedState((current) =>
			current.channel === channel ? { channel, fuse: null, products: [], loadedAtMs: null } : current,
		);
		setIsLoading(true);

		const pending = pendingIndexCache.get(channel);
		const request =
			pending ??
			fetch(`/api/search/index?channel=${encodeURIComponent(channel)}`)
				.then(async (res) => {
					if (!res.ok) {
						throw new Error(`Failed to load search index (${res.status})`);
					}

					const data: unknown = await res.json();
					if (!Array.isArray(data)) {
						throw new Error("Search index response was not an array");
					}

					return data as SearchIndexProduct[];
				})
				.then((data) => {
					const loadedAtMs = Date.now();
					const next = {
						products: data,
						fuse: createFuseInstance(data),
						loadedAtMs,
					};

					resolvedIndexCache.set(channel, next);
					return next;
				});

		if (!pending) {
			pendingIndexCache.set(channel, request);
		}

		request
			.then((data) => {
				if (requestVersion !== requestVersionRef.current) return;
				setLoadedState({
					channel,
					products: data.products,
					fuse: data.fuse,
					loadedAtMs: data.loadedAtMs,
				});
			})
			.catch((err) => {
				if (requestVersion !== requestVersionRef.current) return;
				console.error("[SearchIndex] Failed to load:", err);
			})
			.finally(() => {
				if (pendingIndexCache.get(channel) === request) pendingIndexCache.delete(channel);
				if (requestVersion !== requestVersionRef.current) return;
				setIsLoading(false);
			});
	}, [channel]);

	return { fuse, products, isLoading, ensureLoaded };
}
